/**
 * The calls behind Settings › AutoCount and Settings › Data sources
 * (docs/integration-data-sources-ux-proposal.md §3.1-3.2 in the SVN repo).
 *
 * The server acts on the workspace in the shared client's wsId header and only for a signed-in
 * workspace admin. Every call also names the workspace, as the other settings pages do: in the
 * query of a read or a delete, in the body of a write.
 *
 * Every /api/autocount answer is the page's whole state ({ state, pairing, online,
 * templateModules, modules, agent?, connection?, setup? }), so the page replaces what it shows
 * with whatever comes back. `modules` is each template module: { key, name, emoji, installed,
 * moduleId, moduleName, enabled, updateAvailable, unavailable, tier, locked }. Errors are
 * { error, message }.
 *
 * AutoCount Full (docs/billing-autocount-connector-pricing-proposal.md §7 in the SVN repo): the
 * state also carries `plan` ({ enforced, tier, state, trialAvailable, price, canBuy,
 * hasSubscription, activation, activationDue, activationPaidUtc, activationLinedUp, region,
 * regionConfirmed, ... }). Buying it goes through the api/billing connector routes below, which
 * name the workspace in the body like the other billing writes.
 */
import { commonStore } from "../store/commonStore";

function client() {
  return commonStore.state.client;
}

function scope(workspaceId) {
  return { params: { workspaceId } };
}

function body(workspaceId, extra) {
  return Object.assign({ workspaceId: Number(workspaceId) }, extra || {});
}

/** The AutoCount page's state: start, approve, setup, setting-up, failed or ready. */
export function getAutoCount(workspaceId) {
  return client().get("/api/autocount", scope(workspaceId));
}

/** The connector's one-time code: the current one while it lasts, a new one with `renew`. */
export function createPairing(workspaceId, renew) {
  return client().post("/api/autocount/pairing", body(workspaceId, { renew: Boolean(renew) }));
}

/** Approves the PC with the book it offers; the state is "setup" once the connection is ready. */
export function approveAutoCount(workspaceId, agentId) {
  return client().post("/api/autocount/approve", body(workspaceId, { agentId }));
}

/** "Not our PC": forgets the connector and revokes its token. */
export function rejectAutoCount(workspaceId, agentId) {
  return client().post("/api/autocount/reject", body(workspaceId, { agentId }));
}

/** Runs the setup and answers once it has finished, up to about a minute later. */
export function runAutoCountSetup(workspaceId, options) {
  // { modules: [keys] } makes only those template modules (the others are linked when they are
  // there); with reinstall: true the chosen ones that are there get the template's latest design.
  const modules = options && Array.isArray(options.modules) ? options.modules.filter(Boolean) : [];
  if (!modules.length) return client().post("/api/autocount/setup", body(workspaceId));
  return client().post("/api/autocount/setup", body(workspaceId, { modules, reinstall: Boolean(options.reinstall) }));
}

/**
 * Shows (enabled) or hides a module the setup made: hidden, it leaves the workspace's menus and
 * the module list MCP clients get, and keeps its design. Answers with the page's state.
 */
export function setAutoCountModuleEnabled(workspaceId, key, enabled) {
  const action = enabled ? "/enable" : "/disable";
  return client().post("/api/autocount/modules/" + encodeURIComponent(key) + action, body(workspaceId));
}

/**
 * SS06269: sets the AutoCount starter dashboard up again: the template's shortcuts and charts for
 * the modules the workspace shows. Answers with the page state plus `dashboard` ({ added,
 * replaced, removed, unchanged }). Without `replace` it writes nothing when a changed widget would
 * go back to the template's or an outdated one would be removed: 409 { error: confirm_replace,
 * message, dashboard }. Other 409s (no_modules, modules_hidden, no_widgets) say why in `message`.
 */
export function setUpAutoCountDashboard(workspaceId, replace) {
  return client().post("/api/autocount/dashboard", body(workspaceId, { replace: Boolean(replace) }));
}

/** Disconnects AutoCount; the modules stay. */
export function disconnectAutoCount(workspaceId) {
  return client().delete("/api/autocount", scope(workspaceId));
}

/**
 * The remote off switch for the AutoCount connector: it stays joined and approved but reads and
 * writes nothing until it is turned on again. Without `agentId`, the one serving the connection.
 */
export function turnOffAutoCount(workspaceId, agentId) {
  return client().post("/api/autocount/turn-off", body(workspaceId, { agentId }));
}

/** Turns the AutoCount connector back on; it checks in again within a minute. */
export function turnOnAutoCount(workspaceId, agentId) {
  return client().post("/api/autocount/turn-on", body(workspaceId, { agentId }));
}

/**
 * Writes to the AutoCount book on or off. A book starts read-only; on, the AutoCount modules that can
 * write do, and the connection allows what they write; off, they read only. Answers with the page
 * state plus `writesChange`, what happened to each module.
 */
export function setAutoCountWrites(workspaceId, on) {
  return client().post("/api/autocount/writes", body(workspaceId, { on: Boolean(on) }));
}

/**
 * Tests writes without saving anything: every switch a write to the book passes, on the server and
 * on the PC. Answers { ready, checks: [{ key, ok, detail }], modules: [{ key, name, ok, detail }] }.
 */
export function testAutoCountWrites(workspaceId) {
  return client().post("/api/autocount/writes/test", body(workspaceId));
}

/**
 * Adds a ready-made workflow (e.g. "restock"): creates its modules, or links those made before.
 * Answers with the page state plus `workflow`, what happened to each module. Nothing in AutoCount
 * changes.
 */
export function addAutoCountWorkflow(workspaceId, key) {
  return client().post("/api/autocount/workflows/" + encodeURIComponent(key), body(workspaceId));
}

/**
 * Starts the 14-day AutoCount Full trial, once per workspace and with no card. Answers with the
 * page state; 409 { error: trial_used | no_book | trial_not_needed, message } otherwise.
 */
export function startAutoCountTrial(workspaceId) {
  return client().post("/api/autocount/trial", body(workspaceId));
}

const CONNECTOR_BILLING = "/api/billing/connectors/autocount/";

// `writes`: the line with writes to the book (activation fee doc §8 W-1), the whole price in place of
// the read-only one; false or absent buys the read-only line.

/** No live subscription: a Stripe Checkout for AutoCount Full. Answers { url }. */
export function checkoutAutoCountFull(workspaceId, writes) {
  return client().post(CONNECTOR_BILLING + "checkout", body(workspaceId, { writes: Boolean(writes) }));
}

/**
 * A live subscription: what adding AutoCount Full to it costs, before anything changes.
 * Answers { amountDue, currency, interval, price, startsAt, prorated }, amounts in major units.
 */
export function previewAutoCountFull(workspaceId, writes) {
  return client().post(CONNECTOR_BILLING + "preview", body(workspaceId, { writes: Boolean(writes) }));
}

/** Adds AutoCount Full to the live subscription (or at the trial's end). Answers { status, startsAt }. */
export function addAutoCountFull(workspaceId, writes) {
  return client().post(CONNECTOR_BILLING + "add", body(workspaceId, { writes: Boolean(writes) }));
}

/**
 * What moving the line on the subscription to the one with writes (or back to read-only) would
 * invoice. Answers { status, writes, price, amountDue, startsAt }; changes nothing.
 */
export function previewAutoCountWrites(workspaceId, writes) {
  return client().post(CONNECTOR_BILLING + "writes/preview", body(workspaceId, { writes: Boolean(writes) }));
}

/** Moves the line: to writes prorated on the next invoice, to read-only at once. Answers { status, writes, price }. */
export function changeAutoCountWrites(workspaceId, writes) {
  return client().post(CONNECTOR_BILLING + "writes", body(workspaceId, { writes: Boolean(writes) }));
}

/**
 * Before the first payment, the country the business is registered in ("MY", "SG" or "US" for
 * every other country), which sets the currency every price shows in (activation fee doc A-5a).
 * Answers { region, currency }; 409 { error: region_confirmed | dealer_billed } once it is fixed.
 */
export function setBillingRegion(workspaceId, region) {
  return client().post("/api/billing/region", body(workspaceId, { region }));
}

/** Every data source of the workspace and every connector that joined it. */
export function getSourceConnections(workspaceId) {
  return client().get("/api/source-connections", scope(workspaceId));
}

function agentPath(agentId, action) {
  return "/api/source-connections/agents/" + encodeURIComponent(agentId) + "/" + action;
}

/** Approves a pending connector after its fingerprint was compared with the PC's window. */
export function approveSourceAgent(workspaceId, agentId) {
  return client().post(agentPath(agentId, "approve"), body(workspaceId));
}

/** Turns one connector off: joined and approved still, serving nothing. Answers its view. */
export function turnOffSourceAgent(workspaceId, agentId) {
  return client().post(agentPath(agentId, "turn-off"), body(workspaceId));
}

/** Turns one connector back on. Answers its view. */
export function turnOnSourceAgent(workspaceId, agentId) {
  return client().post(agentPath(agentId, "turn-on"), body(workspaceId));
}

export default {
  getAutoCount,
  createPairing,
  approveAutoCount,
  rejectAutoCount,
  runAutoCountSetup,
  setAutoCountModuleEnabled,
  setUpAutoCountDashboard,
  disconnectAutoCount,
  turnOffAutoCount,
  turnOnAutoCount,
  setAutoCountWrites,
  testAutoCountWrites,
  addAutoCountWorkflow,
  startAutoCountTrial,
  checkoutAutoCountFull,
  previewAutoCountFull,
  addAutoCountFull,
  previewAutoCountWrites,
  changeAutoCountWrites,
  setBillingRegion,
  getSourceConnections,
  approveSourceAgent,
  turnOffSourceAgent,
  turnOnSourceAgent,
};
